
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  ShieldCheck,
  FileText,
  CreditCard,
  CalendarClock,
  BriefcaseBusiness,
  CheckCircle2,
  XCircle,
  MessageCircle,
  ChevronRight,
  ArrowUpRight,
  RotateCcw,
} from "lucide-react";

gsap.registerPlugin(ScrollTrigger);

const sections = [
  {
    id: "overview",
    title: "Overview",
    icon: FileText,
    content: (
      <>
        <p>At Beyond the Analysis, we aim to deliver digital marketing and technology services with transparency, accountability, and a focus on your business objectives.</p>
        <p>Since our services involve strategy, campaign setup, creative production, development, and dedicated working hours, refund eligibility depends on the service purchased, work completed, and terms agreed upon.</p>
      </>
    ),
  },
  {
    id: "eligible",
    title: "When a Refund May Be Available",
    icon: CheckCircle2,
    content: (
      <>
        <p>A refund may be considered in the following circumstances:</p>
        <ul>
          <li>A duplicate payment or incorrect charge has occurred.</li>
          <li>A service has not started and no substantial work or non-recoverable expenses have been incurred.</li>
          <li>We cannot deliver an agreed service and cannot reasonably resolve the issue through an alternative arrangement.</li>
          <li>A refund is provided for under your service agreement or required by applicable law.</li>
        </ul>
        <p>Every request is reviewed individually according to the project terms and applicable legal requirements.</p>
      </>
    ),
  },
  {
    id: "non-refundable",
    title: "Services Generally Not Eligible for Refunds",
    icon: XCircle,
    content: (
      <>
        <p>Unless otherwise agreed in writing or required by law, the following are generally non-refundable once the corresponding work or expense has been incurred:</p>
        <ul>
          <li><strong>Marketing strategy:</strong> Completed audits, consultations, research, and strategic plans.</li>
          <li><strong>Campaign management:</strong> Advertising setup, campaign optimisation, monitoring, and reporting already performed.</li>
          <li><strong>Creative services:</strong> Completed or approved designs, advertising creatives, copywriting, and content.</li>
          <li><strong>Development:</strong> Website design, development, integrations, and completed project milestones.</li>
          <li><strong>Third-party costs:</strong> Advertising spend, hosting, domains, software subscriptions, and licences that cannot be recovered.</li>
        </ul>
        <p>Nothing in this policy excludes consumer rights or remedies that cannot legally be excluded.</p>
      </>
    ),
  },
  {
    id: "advertising",
    title: "Advertising Spend & Campaign Results",
    icon: BriefcaseBusiness,
    content: (
      <>
        <p>Advertising budgets paid to platforms such as Google Ads and Meta Ads are separate from agency management fees unless your agreement states otherwise.</p>
        <p>We do not guarantee specific sales, leads, conversions, search rankings, revenue, or return on ad spend. Campaign performance depends on factors such as competition, budgets, market conditions, offers, and website conversion rates.</p>
        <p>Unsatisfactory campaign performance alone does not automatically qualify for a refund for services already delivered. Advertising platform billing disputes may need to be raised directly with the relevant platform.</p>
      </>
    ),
  },
  {
    id: "subscriptions",
    title: "Monthly Retainers & Recurring Services",
    icon: CalendarClock,
    content: (
      <>
        <p>Monthly marketing services are billed according to the payment schedule and cancellation terms agreed upon before work begins.</p>
        <ul>
          <li>Cancellation requests should be submitted in writing.</li>
          <li>Any agreed notice period will apply.</li>
          <li>Fees for work already performed may remain payable.</li>
          <li>Unused prepaid balances will be assessed according to the agreement and applicable law.</li>
        </ul>
        <p>Cancellation does not automatically entitle a client to a refund for previous billing periods.</p>
      </>
    ),
  },
  {
    id: "cancellation",
    title: "Project Cancellation",
    icon: RotateCcw,
    content: (
      <>
        <p>If you wish to cancel a project, contact our team as soon as possible.</p>
        <p>Depending on the agreed terms, the final amount payable may account for completed milestones, work in progress, committed resources, and non-recoverable third-party costs.</p>
        <p>Any eligible refund will be calculated according to the service agreement and applicable law.</p>
      </>
    ),
  },
  {
    id: "request",
    title: "How to Request a Refund",
    icon: MessageCircle,
    content: (
      <>
        <p>Contact our team by email and include the following details:</p>
        <ul>
          <li>Your name or business name.</li>
          <li>The service or project associated with the payment.</li>
          <li>The payment date and invoice or transaction reference.</li>
          <li>The reason for your refund request.</li>
          <li>Supporting information relevant to your request.</li>
        </ul>
        <p>Please do not include full card numbers, passwords, or sensitive payment credentials in your message.</p>
      </>
    ),
  },
  {
    id: "processing",
    title: "Review & Processing",
    icon: CreditCard,
    content: (
      <>
        <p>We will review your request within a reasonable period and may contact you if additional information is required.</p>
        <p>If approved, refunds will generally be issued to the original payment method where possible. The time required for funds to appear depends on your bank, payment provider, and payment method.</p>
        <p>Any applicable statutory refund deadlines take precedence over general processing estimates in this policy.</p>
      </>
    ),
  },
  {
    id: "updates",
    title: "Policy Updates",
    icon: ShieldCheck,
    content: (
      <p>We may update this policy to reflect changes in our services, business processes, or legal obligations. The latest version will be published on this page. Existing service agreements and applicable law will continue to govern the relevant engagement.</p>
    ),
  },
];

const RefundPolicy = () => {
  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        ".refund-animate",
        { opacity: 0, y: 24 },
        {
          opacity: 1,
          y: 0,
          duration: 0.7,
          stagger: 0.1,
          ease: "power3.out",
        }
      );

      gsap.utils.toArray(".refund-section").forEach((section) => {
        gsap.fromTo(
          section,
          { opacity: 0, y: 20 },
          {
            opacity: 1,
            y: 0,
            duration: 0.6,
            scrollTrigger: {
              trigger: section,
              start: "top 88%",
              once: true,
            },
          }
        );
      });
    });

    return () => ctx.revert();
  }, []);

  const styles = {
    page: {
      "--green": "#a3e635",
      minHeight: "100vh",
      background: "#080d13",
      color: "#f1f5f9",
      fontFamily: "inherit",
      overflowX: "clip",
      position: "relative",
    },
    container: {
      width: "min(1180px, calc(100% - 40px))",
      margin: "0 auto",
    },
    hero: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: "45px",
      padding: "85px 0 70px",
      borderBottom: "1px solid rgba(148,163,184,.16)",
    },
    eyebrow: {
      display: "inline-flex",
      alignItems: "center",
      gap: "9px",
      padding: "9px 14px",
      border: "1px solid rgba(163,230,53,.25)",
      borderRadius: "50px",
      background: "rgba(132,204,22,.07)",
      color: "#a3e635",
      fontSize: "10px",
      fontWeight: 700,
      letterSpacing: "1.4px",
    },
    title: {
      margin: "24px 0 18px",
      fontSize: "clamp(42px, 6vw, 76px)",
      lineHeight: 1.08,
      letterSpacing: "-3px",
      fontWeight: 750,
    },
    description: {
      maxWidth: "620px",
      color: "#a8b4c4",
      fontSize: "15px",
      lineHeight: 1.9,
    },
    green: {
      color: "#a3e635",
    },
    meta: {
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: "14px",
      marginTop: "25px",
      color: "#94a3b8",
      fontSize: "12px",
    },
    heroIcon: {
      width: "210px",
      height: "210px",
      minWidth: "170px",
      display: "grid",
      placeItems: "center",
      border: "1px solid rgba(163,230,53,.25)",
      borderRadius: "50%",
      background: "radial-gradient(circle,rgba(132,204,22,.14),transparent 70%)",
      color: "#a3e635",
      boxShadow: "0 0 70px rgba(132,204,22,.08)",
      margin: "0 auto",
    },
    layout: {
      display: "grid",
      gridTemplateColumns: "260px minmax(0,1fr)",
      gap: "55px",
      alignItems: "start",
      padding: "55px 0 85px",
    },
    sidebar: {
      position: "sticky",
      top: "25px",
      padding: "22px 14px",
      border: "1px solid rgba(148,163,184,.16)",
      borderRadius: "15px",
      background: "#111a25",
    },
    sidebarLabel: {
      display: "block",
      margin: "0 8px 16px",
      color: "#64748b",
      fontSize: "10px",
      fontWeight: 700,
      letterSpacing: "1.5px",
    },
    navLink: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "8px",
      padding: "11px 8px",
      borderRadius: "7px",
      color: "#aab7c7",
      fontSize: "11px",
      lineHeight: 1.5,
      textDecoration: "none",
      transition: "background .2s ease, color .2s ease",
    },
    intro: {
      display: "flex",
      gap: "17px",
      padding: "24px",
      marginBottom: "25px",
      border: "1px solid rgba(163,230,53,.2)",
      borderRadius: "14px",
      background: "linear-gradient(120deg,rgba(132,204,22,.08),#111a25)",
    },
    smallIcon: {
      width: "45px",
      height: "45px",
      minWidth: "45px",
      display: "grid",
      placeItems: "center",
      border: "1px solid rgba(163,230,53,.2)",
      borderRadius: "11px",
      background: "rgba(132,204,22,.08)",
      color: "#a3e635",
    },
    section: {
      padding: "29px 0 32px",
      borderBottom: "1px solid rgba(148,163,184,.16)",
      scrollMarginTop: "25px",
    },
    sectionHeading: {
      display: "flex",
      alignItems: "center",
      gap: "14px",
      marginBottom: "20px",
    },
    sectionNumber: {
      display: "block",
      marginBottom: "5px",
      color: "#84a44e",
      fontSize: "9px",
      fontWeight: 700,
      letterSpacing: "1.5px",
    },
    sectionTitle: {
      margin: 0,
      color: "#f1f5f9",
      fontSize: "clamp(19px,2vw,24px)",
      fontWeight: 650,
      lineHeight: 1.4,
    },
    body: {
      paddingLeft: "59px",
      color: "#9eacbd",
      fontSize: "13px",
      lineHeight: 2,
      overflowWrap: "anywhere",
    },
    contact: {
      display: "flex",
      gap: "18px",
      padding: "28px",
      marginTop: "35px",
      border: "1px solid rgba(163,230,53,.23)",
      borderRadius: "16px",
      background: "radial-gradient(ellipse at top right,rgba(132,204,22,.1),#111a25 65%)",
    },
    footerNote: {
      marginTop: "25px",
      color: "#77879a",
      fontSize: "11px",
      lineHeight: 1.8,
    },
    back: {
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      marginTop: "25px",
      color: "#a8b4c4",
      fontSize: "12px",
      textDecoration: "none",
    },
  };

  return (
    <main style={styles.page}>
      <div style={{
        position: "absolute",
        top: "100px",
        left: "-160px",
        width: "300px",
        height: "300px",
        borderRadius: "50%",
        background: "rgba(132,204,22,.08)",
        filter: "blur(90px)",
        pointerEvents: "none",
        zIndex: 0,
      }} />

      <div style={{ ...styles.container, position: "relative", zIndex: 1 }}>
        <header style={styles.hero}>
          <div style={{ flex: "1 1 450px" }}>
            <div className="refund-animate" style={styles.eyebrow}>
              <ShieldCheck size={16} />
              FAIRNESS. CLARITY. ACCOUNTABILITY.
            </div>

            <h1 className="refund-animate" style={styles.title}>
              Our Refund
              <span style={styles.green}> Policy.</span>
            </h1>

            <p className="refund-animate" style={styles.description}>
              Clear expectations. Transparent terms. A straightforward
              process for handling refund requests and project cancellations.
            </p>

            <div className="refund-animate" style={styles.meta}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
                <CheckCircle2 size={16} color="#a3e635" />
                Transparent service terms
              </span>
              <span>Last updated: October 6, 2026</span>
            </div>
          </div>

          <div style={{ flex: "0 1 230px" }}>
            <div style={styles.heroIcon}>
              <RotateCcw size={75} strokeWidth={1.2} />
            </div>
          </div>
        </header>

        <div style={styles.layout} className="refund-layout">
          <aside style={styles.sidebar} className="refund-sidebar">
            <span style={styles.sidebarLabel}>POLICY GUIDE</span>

            <nav style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {sections.map((section, index) => (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  style={styles.navLink}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(163,230,53,.08)";
                    e.currentTarget.style.color = "#a3e635";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.color = "#aab7c7";
                  }}
                >
                  <span>{String(index + 1).padStart(2, "0")} &nbsp; {section.title}</span>
                  <ChevronRight size={13} />
                </a>
              ))}
            </nav>
          </aside>

          <div style={{ minWidth: 0 }}>
            <section style={styles.intro}>
              <div style={styles.smallIcon}>
                <FileText size={22} />
              </div>
              <div>
                <h2 style={{ margin: "0 0 9px", fontSize: "17px" }}>
                  Our commitment to fair service
                </h2>
                <p style={{ margin: 0, color: "#a5b2c2", fontSize: "13px", lineHeight: 1.9 }}>
                  Every engagement is different. We assess refund requests
                  according to the work delivered, your agreed service terms,
                  and applicable legal requirements.
                </p>
              </div>
            </section>

            {sections.map((section, index) => {
              const Icon = section.icon;

              return (
                <section
                  key={section.id}
                  id={section.id}
                  className="refund-section"
                  style={styles.section}
                >
                  <div style={styles.sectionHeading}>
                    <div style={styles.smallIcon}>
                      <Icon size={21} />
                    </div>
                    <div>
                      <span style={styles.sectionNumber}>
                        SECTION {String(index + 1).padStart(2, "0")}
                      </span>
                      <h2 style={styles.sectionTitle}>{section.title}</h2>
                    </div>
                  </div>

                  <div
                    style={styles.body}
                    className="refund-policy-body"
                  >
                    {section.content}
                  </div>
                </section>
              );
            })}

            <section style={styles.contact}>
              <div style={styles.smallIcon}>
                <MessageCircle size={24} />
              </div>

              <div style={{ minWidth: 0 }}>
                <span style={{
                  color: "#a3e635",
                  fontSize: "10px",
                  fontWeight: 700,
                  letterSpacing: "1.4px",
                }}>
                  NEED HELP WITH A PAYMENT?
                </span>

                <h2 style={{ margin: "10px 0", fontSize: "23px" }}>
                  Let's resolve it together.
                </h2>

                <p style={{
                  color: "#9eacbd",
                  fontSize: "13px",
                  lineHeight: 1.9,
                  margin: "0 0 15px",
                }}>
                  Contact our team with your project and payment details
                  so we can review your request.
                </p>

                <a
                  href="mailto:YOUR_BUSINESS_EMAIL"
                  style={{
                    color: "#a3e635",
                    fontSize: "13px",
                    fontWeight: 650,
                    textDecoration: "none",
                    overflowWrap: "anywhere",
                  }}
                >
                  YOUR_BUSINESS_EMAIL <ArrowUpRight size={15} />
                </a>
              </div>
            </section>

            <p style={styles.footerNote}>
              This policy does not limit any consumer rights or protections
              that cannot legally be excluded. Please review it against your
              actual business practices and applicable law before publishing.
            </p>

            <Link to="/" style={styles.back}>
              <ChevronRight size={15} style={{ transform: "rotate(180deg)" }} />
              Back to home
            </Link>
          </div>
        </div>
      </div>

      <style>{`
        .refund-policy-body p {
          margin: 0 0 15px;
        }

        .refund-policy-body p:last-child {
          margin-bottom: 0;
        }

        .refund-policy-body ul {
          display: grid;
          gap: 9px;
          list-style: none;
          padding: 0;
          margin: 16px 0;
        }

        .refund-policy-body li {
          position: relative;
          padding-left: 21px;
        }

        .refund-policy-body li::before {
          position: absolute;
          content: "";
          top: 10px;
          left: 2px;
          width: 7px;
          height: 7px;
          border-radius: 2px;
          background: #a3e635;
          transform: rotate(45deg);
        }

        .refund-policy-body strong {
          color: #dbe5ee;
        }

        @media (max-width: 850px) {
          .refund-layout {
            grid-template-columns: 1fr !important;
            gap: 28px !important;
            padding-top: 35px !important;
          }

          .refund-sidebar {
            position: static !important;
          }

          .refund-sidebar nav {
            display: grid !important;
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
        }

        @media (max-width: 520px) {
          .refund-policy-body {
            padding-left: 0 !important;
          }

          .refund-sidebar nav {
            grid-template-columns: 1fr !important;
          }

          .refund-layout {
            padding-bottom: 55px !important;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .refund-section,
          .refund-animate {
            animation: none !important;
            transition: none !important;
          }
        }
      `}</style>
    </main>
  );
};

export default RefundPolicy;
